import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Check, ChevronDown, ChevronRight, Package } from 'lucide-react';
import type { SiteBlock, BlockSettings, BackgroundSettings as BgSettings, SitePageType, SelectedProductRef } from '../../types/site';
import { BLOCK_TYPE_LABELS } from '../../types/site';
import type { Form } from '../../types/forms';
import type { Profile } from '../../lib/supabase';
import type { ProductWithMedia } from '../../types/products';
import { ImageUpload } from '../ImageUpload';
import { BackgroundSettings } from './BackgroundSettings';
import { getAllProductsByCategory, type ProductsByCategory } from '../../services/productService';
import { IconPickerButton } from '../IconPicker';
import { logger } from '../../lib/logger';

interface BlockEditorModalProps {
  block: SiteBlock | null;
  forms: Form[];
  pageContext?: { page_type: SitePageType; page_title: string };
  profile?: Profile | null;
  onClose: () => void;
  onSave: (blockId: string, settings: BlockSettings, showInMenu: boolean, menuLabel: string | null) => void;
}

const DEFAULT_BACKGROUND: BgSettings = {
  type: 'none',
  color: null,
  gradient: null,
  imageUrl: null,
};

export function BlockEditorModal({ block, forms, onClose, onSave }: BlockEditorModalProps) {
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [showInMenu, setShowInMenu] = useState(true);
  const [menuLabel, setMenuLabel] = useState('');

  // Product Showcase specific state
  const [productCategories, setProductCategories] = useState<ProductsByCategory[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productMap, setProductMap] = useState<Record<string, ProductWithMedia>>({});

  useEffect(() => {
    if (block) {
      setSettings(block.settings as Record<string, unknown>);
      setShowInMenu(block.show_in_menu);
      setMenuLabel(block.menu_label || '');

      // Load products for PRODUCT_SHOWCASE block
      if (block.type === 'PRODUCT_SHOWCASE') {
        setProductsLoading(true);
        getAllProductsByCategory()
          .then((categories) => {
            setProductCategories(categories);
            // Build a map of productId -> ProductWithMedia for quick lookup
            const map: Record<string, ProductWithMedia> = {};
            categories.forEach((cat) => {
              cat.products.forEach((p) => {
                map[p.id] = p;
              });
            });
            setProductMap(map);
            // Expand first category by default
            if (categories.length > 0) {
              setExpandedCategories(new Set([categories[0].category]));
            }
          })
          .catch((err) => {
            logger.error('Failed to load products:', err);
          })
          .finally(() => {
            setProductsLoading(false);
          });
      }
    }
  }, [block]);

  if (!block) return null;

  const handleChange = (key: string, value: unknown) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleBackgroundChange = (bg: BgSettings) => {
    handleChange('background', bg);
  };

  const handleSave = () => {
    onSave(block.id, settings as BlockSettings, showInMenu, menuLabel || null);
    onClose();
  };

  const background = (settings.background as BgSettings) || DEFAULT_BACKGROUND;

  const renderEditor = () => {
    switch (block.type) {
      case 'HERO':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Untertitel</label>
              <input
                type="text"
                value={(settings.subtitle as string) || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Bild</label>
              <div className="max-w-md">
                <ImageUpload
                  value={(settings.imageUrl as string) || ''}
                  onChange={(url) => handleChange('imageUrl', url)}
                  aspectRatio="16:9"
                  placeholder="Bild hochladen"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Button-Text</label>
              <input
                type="text"
                value={(settings.buttonLabel as string) || ''}
                onChange={(e) => handleChange('buttonLabel', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Layout</label>
              <select
                value={(settings.layout as string) || 'image-right'}
                onChange={(e) => handleChange('layout', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="image-left">Bild links</option>
                <option value="image-right">Bild rechts</option>
                <option value="center">Zentriert</option>
              </select>
            </div>
          </div>
        );

      case 'ABOUT':
      case 'STORY':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Text</label>
              <textarea
                value={(settings.text as string) || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                rows={5}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Bild (optional)</label>
              <div className="max-w-md">
                <ImageUpload
                  value={(settings.imageUrl as string) || ''}
                  onChange={(url) => handleChange('imageUrl', url)}
                  aspectRatio="4:3"
                  placeholder="Bild hochladen"
                />
              </div>
            </div>
          </div>
        );

      case 'BENEFITS':
      case 'HIGHLIGHTS': {
        const items = (settings.items as { icon: string; title: string; text: string }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Elemente</label>
              {items.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-3">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Icon</label>
                      <IconPickerButton
                        value={item.icon}
                        onChange={(iconName) => {
                          const newItems = [...items];
                          newItems[index].icon = iconName;
                          handleChange('items', newItems);
                        }}
                        placeholder="Icon wählen"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Titel</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => {
                          const newItems = [...items];
                          newItems[index].title = e.target.value;
                          handleChange('items', newItems);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Beschreibung</label>
                    <input
                      type="text"
                      value={item.text}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[index].text = e.target.value;
                        handleChange('items', newItems);
                      }}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = items.filter((_, i) => i !== index);
                      handleChange('items', newItems);
                    }}
                    className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  handleChange('items', [...items, { icon: '✨', title: '', text: '' }]);
                }}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                + Element hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'IMAGE_TEXT':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Text</label>
              <textarea
                value={(settings.text as string) || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Bild</label>
              <div className="max-w-md">
                <ImageUpload
                  value={(settings.imageUrl as string) || ''}
                  onChange={(url) => handleChange('imageUrl', url)}
                  aspectRatio="4:3"
                  placeholder="Bild hochladen"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Layout</label>
              <select
                value={(settings.layout as string) || 'image-right'}
                onChange={(e) => handleChange('layout', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="image-left">Bild links</option>
                <option value="image-right">Bild rechts</option>
              </select>
            </div>
          </div>
        );

      case 'TESTIMONIALS': {
        const testimonials = (settings.testimonials as { name: string; text: string; imageUrl?: string }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Erfahrungsberichte</label>
              {testimonials.map((t, index) => (
                <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-3">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Name</label>
                      <input
                        type="text"
                        value={t.name}
                        onChange={(e) => {
                          const newTestimonials = [...testimonials];
                          newTestimonials[index].name = e.target.value;
                          handleChange('testimonials', newTestimonials);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Text</label>
                      <textarea
                        value={t.text}
                        onChange={(e) => {
                          const newTestimonials = [...testimonials];
                          newTestimonials[index].text = e.target.value;
                          handleChange('testimonials', newTestimonials);
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Bild (optional)</label>
                      <ImageUpload
                        value={t.imageUrl || ''}
                        onChange={(url) => {
                          const newTestimonials = [...testimonials];
                          newTestimonials[index].imageUrl = url;
                          handleChange('testimonials', newTestimonials);
                        }}
                        aspectRatio="square"
                        placeholder="Bild"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newTestimonials = testimonials.filter((_, i) => i !== index);
                      handleChange('testimonials', newTestimonials);
                    }}
                    className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  handleChange('testimonials', [...testimonials, { name: '', text: '', imageUrl: '' }]);
                }}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                + Erfahrungsbericht hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'FORM':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Formular auswählen</label>
              <select
                value={(settings.formId as string) || ''}
                onChange={(e) => handleChange('formId', e.target.value || null)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="">Kein Formular ausgewählt</option>
                {forms.filter(f => f.is_published).map((form) => (
                  <option key={form.id} value={form.id}>
                    {form.name}
                  </option>
                ))}
              </select>
              {forms.filter(f => f.is_published).length === 0 && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Du hast noch keine veröffentlichten Formulare. Erstelle zuerst ein Formular unter "Formulare".
                </p>
              )}
            </div>
          </div>
        );

      case 'CTA':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Button-Text</label>
              <input
                type="text"
                value={(settings.buttonLabel as string) || ''}
                onChange={(e) => handleChange('buttonLabel', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );

      case 'TEXT':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Text</label>
              <textarea
                value={(settings.text as string) || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                rows={6}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );

      case 'IMAGE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Bild</label>
              <div className="max-w-md">
                <ImageUpload
                  value={(settings.imageUrl as string) || ''}
                  onChange={(url) => handleChange('imageUrl', url)}
                  aspectRatio="auto"
                  placeholder="Bild hochladen"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Beschriftung (optional)</label>
              <input
                type="text"
                value={(settings.caption as string) || ''}
                onChange={(e) => handleChange('caption', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );

      case 'VIDEO':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Video-URL (YouTube)</label>
              <input
                type="url"
                value={(settings.videoUrl as string) || ''}
                onChange={(e) => handleChange('videoUrl', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel (optional)</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );

      case 'FAQ': {
        const faqItems = (settings.items as { question: string; answer: string }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Fragen & Antworten</label>
              {faqItems.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-3">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Frage</label>
                      <input
                        type="text"
                        value={item.question}
                        onChange={(e) => {
                          const newItems = [...faqItems];
                          newItems[index].question = e.target.value;
                          handleChange('items', newItems);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Antwort</label>
                      <textarea
                        value={item.answer}
                        onChange={(e) => {
                          const newItems = [...faqItems];
                          newItems[index].answer = e.target.value;
                          handleChange('items', newItems);
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = faqItems.filter((_, i) => i !== index);
                      handleChange('items', newItems);
                    }}
                    className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  handleChange('items', [...faqItems, { question: '', answer: '' }]);
                }}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                + Frage hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'WHATSAPP_BUTTON':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Telefonnummer (mit Ländervorwahl)</label>
              <input
                type="text"
                value={(settings.phoneNumber as string) || ''}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="4917612345678"
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Vorgefertigte Nachricht</label>
              <textarea
                value={(settings.message as string) || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={2}
                placeholder="Hallo, ich interessiere mich für..."
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Button-Text</label>
              <input
                type="text"
                value={(settings.buttonLabel as string) || ''}
                onChange={(e) => handleChange('buttonLabel', e.target.value)}
                placeholder="WhatsApp Nachricht senden"
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );

      case 'CUSTOM_HTML':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">HTML-Code</label>
              <textarea
                value={(settings.html as string) || ''}
                onChange={(e) => handleChange('html', e.target.value)}
                rows={10}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                Hinweis: Script-Tags werden aus Sicherheitsgründen entfernt.
              </p>
            </div>
          </div>
        );

      case 'TIMELINE': {
        const timelineItems = (settings.items as { yearOrStep: string; title: string; description: string; icon: string | null }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Untertitel</label>
              <input
                type="text"
                value={(settings.subtitle as string) || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Timeline-Einträge</label>
              {timelineItems.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Jahr/Schritt</label>
                      <input
                        type="text"
                        value={item.yearOrStep}
                        onChange={(e) => {
                          const newItems = [...timelineItems];
                          newItems[index].yearOrStep = e.target.value;
                          handleChange('items', newItems);
                        }}
                        placeholder="z.B. 2020"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Icon</label>
                      <IconPickerButton
                        value={item.icon}
                        onChange={(iconName) => {
                          const newItems = [...timelineItems];
                          newItems[index].icon = iconName || null;
                          handleChange('items', newItems);
                        }}
                        placeholder="Icon wählen"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Titel</label>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => {
                        const newItems = [...timelineItems];
                        newItems[index].title = e.target.value;
                        handleChange('items', newItems);
                      }}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Beschreibung</label>
                    <textarea
                      value={item.description}
                      onChange={(e) => {
                        const newItems = [...timelineItems];
                        newItems[index].description = e.target.value;
                        handleChange('items', newItems);
                      }}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange('items', timelineItems.filter((_, i) => i !== index))}
                    className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleChange('items', [...timelineItems, { yearOrStep: '', title: '', description: '', icon: null }])}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Eintrag hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'PROBLEM_SOLUTION': {
        const problems = (settings.problems as { text: string; icon: string | null }[]) || [];
        const solutions = (settings.solutions as { text: string; icon: string | null }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Layout</label>
              <select
                value={(settings.layout as string) || 'left-right'}
                onChange={(e) => handleChange('layout', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="left-right">Nebeneinander</option>
                <option value="stacked">Übereinander</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">Probleme</label>
              {problems.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2 items-start">
                  <div className="w-32 flex-shrink-0">
                    <IconPickerButton
                      value={item.icon}
                      onChange={(iconName) => {
                        const newP = [...problems];
                        newP[index].icon = iconName || null;
                        handleChange('problems', newP);
                      }}
                      placeholder="Icon"
                    />
                  </div>
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => {
                      const newP = [...problems];
                      newP[index].text = e.target.value;
                      handleChange('problems', newP);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    placeholder="Problem beschreiben"
                  />
                  <button
                    type="button"
                    onClick={() => handleChange('problems', problems.filter((_, i) => i !== index))}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleChange('problems', [...problems, { text: '', icon: 'X' }])}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Problem hinzufügen
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-green-600 mb-2">Lösungen</label>
              {solutions.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2 items-start">
                  <div className="w-32 flex-shrink-0">
                    <IconPickerButton
                      value={item.icon}
                      onChange={(iconName) => {
                        const newS = [...solutions];
                        newS[index].icon = iconName || null;
                        handleChange('solutions', newS);
                      }}
                      placeholder="Icon"
                    />
                  </div>
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => {
                      const newS = [...solutions];
                      newS[index].text = e.target.value;
                      handleChange('solutions', newS);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    placeholder="Lösung beschreiben"
                  />
                  <button
                    type="button"
                    onClick={() => handleChange('solutions', solutions.filter((_, i) => i !== index))}
                    className="p-2 text-green-500 hover:bg-green-50 rounded-lg flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleChange('solutions', [...solutions, { text: '', icon: 'Check' }])}
                className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Lösung hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'TEAM': {
        const members = (settings.members as { name: string; role: string; bio: string; imageUrl: string }[]) || [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Untertitel</label>
              <input
                type="text"
                value={(settings.subtitle as string) || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Spalten</label>
              <select
                value={String((settings.columns as number) || 3)}
                onChange={(e) => handleChange('columns', parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="2">2 Spalten</option>
                <option value="3">3 Spalten</option>
                <option value="4">4 Spalten</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Teammitglieder</label>
              {members.map((member, index) => (
                <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-3">
                  <div className="flex gap-4 mb-3">
                    <div className="w-20">
                      <ImageUpload
                        value={member.imageUrl || ''}
                        onChange={(url) => {
                          const newM = [...members];
                          newM[index].imageUrl = url;
                          handleChange('members', newM);
                        }}
                        aspectRatio="square"
                        placeholder="Foto"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Name</label>
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => {
                            const newM = [...members];
                            newM[index].name = e.target.value;
                            handleChange('members', newM);
                          }}
                          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Rolle</label>
                        <input
                          type="text"
                          value={member.role}
                          onChange={(e) => {
                            const newM = [...members];
                            newM[index].role = e.target.value;
                            handleChange('members', newM);
                          }}
                          placeholder="z.B. CEO, Designer"
                          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Bio</label>
                    <textarea
                      value={member.bio}
                      onChange={(e) => {
                        const newM = [...members];
                        newM[index].bio = e.target.value;
                        handleChange('members', newM);
                      }}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange('members', members.filter((_, i) => i !== index))}
                    className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleChange('members', [...members, { name: '', role: '', bio: '', imageUrl: '' }])}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Mitglied hinzufügen
              </button>
            </div>
          </div>
        );
      }

      case 'PRODUCT_SHOWCASE': {
        const selectedProducts = (settings.selectedProducts as SelectedProductRef[]) || [];
        const selectedProductIds = new Set(selectedProducts.map((p) => p.productId));

        const toggleProduct = (product: ProductWithMedia) => {
          const isSelected = selectedProductIds.has(product.id);
          if (isSelected) {
            // Remove product
            handleChange(
              'selectedProducts',
              selectedProducts.filter((p) => p.productId !== product.id)
            );
          } else {
            // Add product with defaults
            handleChange('selectedProducts', [
              ...selectedProducts,
              {
                productId: product.id,
                customName: undefined,
                customDescription: undefined,
                customBadge: undefined,
              },
            ]);
          }
        };

        const updateSelectedProduct = (productId: string, field: keyof SelectedProductRef, value: string) => {
          handleChange(
            'selectedProducts',
            selectedProducts.map((p) =>
              p.productId === productId ? { ...p, [field]: value || undefined } : p
            )
          );
        };

        const toggleCategory = (category: string) => {
          const newExpanded = new Set(expandedCategories);
          if (newExpanded.has(category)) {
            newExpanded.delete(category);
          } else {
            newExpanded.add(category);
          }
          setExpandedCategories(newExpanded);
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Untertitel</label>
              <input
                type="text"
                value={(settings.subtitle as string) || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Spalten</label>
              <select
                value={String((settings.columns as number) || 3)}
                onChange={(e) => handleChange('columns', parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="2">2 Spalten</option>
                <option value="3">3 Spalten</option>
              </select>
            </div>

            {/* Product Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Produkte auswählen
                {selectedProducts.length > 0 && (
                  <span className="ml-2 text-teal-600">({selectedProducts.length} ausgewählt)</span>
                )}
              </label>

              {productsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500 dark:text-slate-400">
                  <div className="animate-spin w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full mr-2" />
                  Produkte werden geladen...
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                  {productCategories.map((category) => (
                    <div key={category.category} className="border-b border-gray-200 dark:border-slate-700 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => toggleCategory(category.category)}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">{category.category}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-slate-400">
                            {category.products.filter((p) => selectedProductIds.has(p.id)).length}/{category.products.length}
                          </span>
                          {expandedCategories.has(category.category) ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {expandedCategories.has(category.category) && (
                        <div className="divide-y divide-gray-100 dark:divide-slate-700">
                          {category.products.map((product) => {
                            const isSelected = selectedProductIds.has(product.id);
                            return (
                              <div
                                key={product.id}
                                className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                                  isSelected ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                                }`}
                                onClick={() => toggleProduct(product)}
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected
                                      ? 'bg-teal-600 border-teal-600'
                                      : 'border-gray-300 dark:border-slate-500'
                                  }`}
                                >
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-600 flex-shrink-0">
                                  {product.media?.[0]?.url ? (
                                    <img
                                      src={product.media[0].url}
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Package className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {product.name}
                                  </p>
                                  {product.short_tagline && (
                                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                      {product.short_tagline}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Products - Customization */}
            {selectedProducts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Ausgewählte Produkte anpassen
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                  Leer lassen, um die Standard-Werte aus dem Produktkatalog zu verwenden.
                </p>
                <div className="space-y-3">
                  {selectedProducts.map((selected) => {
                    const product = productMap[selected.productId];
                    if (!product) return null;

                    return (
                      <div
                        key={selected.productId}
                        className="border border-gray-200 dark:border-slate-700 rounded-xl p-4"
                      >
                        <div className="flex gap-4 mb-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-600 flex-shrink-0">
                            {product.media?.[0]?.url ? (
                              <img
                                src={product.media[0].url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 space-y-2">
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">
                                Name <span className="text-gray-400">(Original: {product.name})</span>
                              </label>
                              <input
                                type="text"
                                value={selected.customName || ''}
                                onChange={(e) =>
                                  updateSelectedProduct(selected.productId, 'customName', e.target.value)
                                }
                                placeholder={product.name}
                                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">
                                Badge (optional)
                              </label>
                              <input
                                type="text"
                                value={selected.customBadge || ''}
                                onChange={(e) =>
                                  updateSelectedProduct(selected.productId, 'customBadge', e.target.value)
                                }
                                placeholder="z.B. Neu, Bestseller"
                                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">
                            Beschreibung <span className="text-gray-400">(Original: {product.short_tagline || '-'})</span>
                          </label>
                          <textarea
                            value={selected.customDescription || ''}
                            onChange={(e) =>
                              updateSelectedProduct(selected.productId, 'customDescription', e.target.value)
                            }
                            placeholder={product.short_tagline || 'Beschreibung eingeben...'}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleProduct(product)}
                          className="mt-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Entfernen
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'ADVANCED_VIDEO':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Video-Typ</label>
              <select
                value={(settings.videoType as string) || 'youtube'}
                onChange={(e) => handleChange('videoType', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                <option value="youtube">YouTube</option>
                <option value="upload">Eigenes Video (URL)</option>
              </select>
            </div>
            {(settings.videoType as string) === 'youtube' || !settings.videoType ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">YouTube-URL</label>
                <input
                  type="url"
                  value={(settings.youtubeUrl as string) || ''}
                  onChange={(e) => handleChange('youtubeUrl', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Video-URL</label>
                <input
                  type="url"
                  value={(settings.videoUrl as string) || ''}
                  onChange={(e) => handleChange('videoUrl', e.target.value)}
                  placeholder="https://example.com/video.mp4"
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                />
              </div>
            )}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(settings.autoplay as boolean) || false}
                  onChange={(e) => handleChange('autoplay', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-teal-600 focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">Autoplay</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(settings.rounded as boolean) !== false}
                  onChange={(e) => handleChange('rounded', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-teal-600 focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">Abgerundete Ecken</span>
              </label>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel</label>
              <input
                type="text"
                value={(settings.title as string) || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !mt-0">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-7xl max-h-[85vh] overflow-hidden flex flex-col m-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {BLOCK_TYPE_LABELS[block.type]} bearbeiten
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {renderEditor()}

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Hintergrund</h3>
            <BackgroundSettings value={background} onChange={handleBackgroundChange} />
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Menü-Einstellungen</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInMenu}
                  onChange={(e) => setShowInMenu(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 dark:border-slate-600 text-teal-600 focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">Im Menü anzeigen</span>
              </label>

              {showInMenu && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Menü-Titel (optional)
                  </label>
                  <input
                    type="text"
                    value={menuLabel}
                    onChange={(e) => setMenuLabel(e.target.value)}
                    placeholder={`Standard: ${BLOCK_TYPE_LABELS[block.type]}`}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Wenn leer, wird der Standard-Name verwendet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium"
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
